/** What one activity's event makes another activity do (`RUN-008`). */

import { useState } from "react";
import type { EventBinding } from "@nebulize/sdk";

import { Accordion, Button, EmptyState, Input, Stack, StatusBadge, Switch, Toolbar } from "@/components/ui";

import { Mono } from "../components/Text";
import type { AddedActivityRow } from "../types";
import type { SpaceActivities } from "../spaces/lib/queries";

import { spaceUnfedTriggers } from "./lib/readiness";
import { TrayRow } from "./tray/TrayRow";
import { Section } from "./tray/Section";
import { useRewire, useSwitchWire, useUnwire, useWire, useWires } from "./lib/wires";
import { WireForm } from "./WireForm";

/** Groups open by default at or under this many wires on the space; past it, a group opens on
 * click. A small space still reads exactly as a flat list; a large one reads as its shape. */
const GROUP_COLLAPSE_THRESHOLD = 8;

function nameOf(rows: readonly AddedActivityRow[], addedActivityId: string): string {
  return rows.find((one) => one.added_activity_id === addedActivityId)?.name ?? addedActivityId;
}

function matchWire(binding: EventBinding, rows: readonly AddedActivityRow[], needle: string): boolean {
  const haystack = [
    nameOf(rows, binding.source_added_activity_id),
    nameOf(rows, binding.target_added_activity_id),
    binding.export,
    binding.trigger,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

/** The form that authors one wire: a source export, a target trigger, and the mapping. */
function NewWire({
  spaceId,
  activities,
  wires,
  onDone,
}: {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
  readonly wires: readonly EventBinding[];
  readonly onDone: () => void;
}) {
  const wire = useWire(spaceId);

  return (
    <WireForm
      activities={activities}
      wires={wires}
      submitLabel="Wire it"
      pending={wire.isPending}
      errorMessage={wire.error?.message}
      onCancel={onDone}
      onSubmit={(value) => {
        wire.mutate(
          {
            source_added_activity_id: value.source,
            export: value.export,
            target_added_activity_id: value.target,
            trigger: value.trigger,
            parameters: value.parameters,
          },
          { onSuccess: onDone },
        );
      }}
    />
  );
}

/** Rebind one wire in place. Neither end moves, so only the export, trigger and mapping change. */
function Rebind({
  spaceId,
  activities,
  wires,
  binding,
  onDone,
}: {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
  readonly wires: readonly EventBinding[];
  readonly binding: EventBinding;
  readonly onDone: () => void;
}) {
  const rewire = useRewire(spaceId);

  return (
    <WireForm
      activities={activities}
      wires={wires}
      locked={{ source: binding.source_added_activity_id, target: binding.target_added_activity_id }}
      excludeBindingId={binding.binding_id}
      initialExport={binding.export}
      initialTrigger={binding.trigger}
      submitLabel="Rebind"
      pending={rewire.isPending}
      errorMessage={rewire.error?.message}
      onCancel={onDone}
      onSubmit={(value) => {
        rewire.mutate(
          {
            bindingId: binding.binding_id,
            export: value.export,
            trigger: value.trigger,
            parameters: value.parameters,
          },
          { onSuccess: onDone },
        );
      }}
    />
  );
}

/** One wire inside its group: the export and trigger it carries. The source and target already
 * name themselves in the group header, so neither repeats here. */
function WireLine({
  spaceId,
  activities,
  wires,
  binding,
}: {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
  readonly wires: readonly EventBinding[];
  readonly binding: EventBinding;
}) {
  const unwire = useUnwire(spaceId);
  const flip = useSwitchWire(spaceId);
  const [rebinding, setRebinding] = useState(false);
  const enabled = binding.enabled !== false;

  return (
    <TrayRow
      dim={!enabled}
      title={<Mono>{`${binding.export} -> ${binding.trigger}`}</Mono>}
      badge={
        binding.origin === "proposed" ? <StatusBadge tone="info">from a bundle</StatusBadge> : undefined
      }
      meta={
        binding.disabled_reason == null || binding.disabled_reason === ""
          ? undefined
          : binding.disabled_reason
      }
      actions={
        <>
          <Switch
            label="Enabled"
            checked={enabled}
            disabled={flip.isPending}
            onChange={(next) => {
              flip.mutate({ bindingId: binding.binding_id, enabled: next });
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Rebind the wire from ${binding.export}`}
            onClick={() => {
              setRebinding((was) => !was);
            }}
          >
            {rebinding ? "Done" : "Rebind"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Remove the wire from ${binding.export}`}
            loading={unwire.isPending}
            onClick={() => {
              unwire.mutate(binding.binding_id);
            }}
          >
            Remove
          </Button>
        </>
      }
      detail={
        rebinding ? (
          <Rebind
            spaceId={spaceId}
            activities={activities}
            wires={wires}
            binding={binding}
            onDone={() => {
              setRebinding(false);
            }}
          />
        ) : undefined
      }
    />
  );
}

interface WireGroup {
  readonly id: string;
  readonly sourceId: string;
  readonly targetId: string;
  readonly sourceName: string;
  readonly targetName: string;
  readonly bindings: EventBinding[];
}

/** Every wire grouped by the activity pair it runs between, in the order those activities were
 * added: source first, target second. This is the fact an owner navigates by, not the flat list. */
function groupWires(rows: readonly AddedActivityRow[], wires: readonly EventBinding[]): WireGroup[] {
  const order = new Map(rows.map((row, index) => [row.added_activity_id, index] as const));
  const groups = new Map<string, WireGroup>();

  for (const binding of wires) {
    const id = `${binding.source_added_activity_id}:${binding.target_added_activity_id}`;
    const existing = groups.get(id);
    if (existing === undefined) {
      groups.set(id, {
        id,
        sourceId: binding.source_added_activity_id,
        targetId: binding.target_added_activity_id,
        sourceName: nameOf(rows, binding.source_added_activity_id),
        targetName: nameOf(rows, binding.target_added_activity_id),
        bindings: [binding],
      });
    } else {
      existing.bindings.push(binding);
    }
  }

  for (const group of groups.values()) {
    group.bindings.sort(
      (a, b) => a.export.localeCompare(b.export) || a.trigger.localeCompare(b.trigger),
    );
  }

  return Array.from(groups.values()).sort((a, b) => {
    const bySource = (order.get(a.sourceId) ?? 0) - (order.get(b.sourceId) ?? 0);
    return bySource !== 0 ? bySource : (order.get(a.targetId) ?? 0) - (order.get(b.targetId) ?? 0);
  });
}

/** One source-to-target pair, collapsible, carrying a count badge and its compact wire lines. */
function WireGroupCard({
  spaceId,
  activities,
  wires,
  group,
  open,
}: {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
  readonly wires: readonly EventBinding[];
  readonly group: WireGroup;
  /** Read once, at mount: the panel remounts this when the open rule itself changes. */
  readonly open: boolean;
}) {
  return (
    <Accordion
      key={open ? "open" : "closed"}
      {...(open ? { defaultOpenId: group.id } : {})}
      items={[
        {
          id: group.id,
          title: (
            <span className="cx-wire-group-title">
              {group.sourceName}
              <span className="cx-mapping-arrow" aria-hidden="true">
                {" -> "}
              </span>
              {group.targetName}
            </span>
          ),
          meta: (
            <StatusBadge tone="neutral">
              {`${String(group.bindings.length)} ${group.bindings.length === 1 ? "wire" : "wires"}`}
            </StatusBadge>
          ),
          content: (
            <Stack gap={2}>
              {group.bindings.map((binding) => (
                <WireLine
                  key={binding.binding_id}
                  spaceId={spaceId}
                  activities={activities}
                  wires={wires}
                  binding={binding}
                />
              ))}
            </Stack>
          ),
        },
      ]}
    />
  );
}

/** Every wire on this space, grouped by the activities it runs between, and the form that adds
 * one. */
export function WiresPanel({
  spaceId,
  activities,
}: {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const wires = useWires(spaceId);
  const rows = wires.data ?? [];
  const needle = query.trim().toLowerCase();
  const filtering = needle !== "";
  const shown = filtering ? rows.filter((one) => matchWire(one, activities.rows, needle)) : rows;
  const groups = groupWires(activities.rows, shown);
  const openByDefault = rows.length <= GROUP_COLLAPSE_THRESHOLD;

  const unfed =
    wires.isPending || activities.manifestsPending
      ? []
      : spaceUnfedTriggers(activities.rows, activities, rows);

  return (
    <Stack gap={3}>
      {rows.length === 0 ? (
        <EmptyState bare headline="Nothing is wired" />
      ) : (
        <>
          <Input
            value={query}
            placeholder="Filter wires"
            aria-label="Filter wires"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          {groups.length === 0 ? (
            <EmptyState bare headline="No matches" />
          ) : (
            <Stack gap={2}>
              {groups.map((group) => (
                <WireGroupCard
                  key={group.id}
                  spaceId={spaceId}
                  activities={activities}
                  wires={rows}
                  group={group}
                  open={filtering || openByDefault}
                />
              ))}
            </Stack>
          )}
        </>
      )}

      {unfed.length === 0 ? null : (
        <Section title="Unwired triggers">
          <Stack gap={2}>
            {unfed.flatMap((one) =>
              one.triggers.map((trigger) => (
                <TrayRow
                  key={`${one.row.added_activity_id}:${trigger.name}`}
                  title={one.row.name}
                  meta={<Mono>{trigger.name}</Mono>}
                />
              )),
            )}
          </Stack>
        </Section>
      )}

      {adding ? (
        <NewWire
          spaceId={spaceId}
          activities={activities}
          wires={rows}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : (
        <Toolbar>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAdding(true);
            }}
          >
            Wire two activities
          </Button>
        </Toolbar>
      )}
    </Stack>
  );
}
